/**
 * connectionStats (Welle 7a, fixed in 7a2)
 *
 * Liest aus RTCPeerConnection.getStats() die sicherheits- und verbindungsrelevanten
 * Kennzahlen fürs Verschlüsselungs-Panel:
 *  - Verbindungstyp (direkt host/srflx/prflx vs. relay über TURN)
 *  - DTLS-Transport: Version, Cipher (best-effort, nicht jeder Browser liefert sie)
 *  - SRTP-Medien-Cipher (best-effort)
 *  - RTT
 *
 * WICHTIG: WebRTC-Medien sind IMMER DTLS-SRTP-verschlüsselt — das lässt sich nicht
 * abschalten. Das "gesichert"-Flag basiert daher auf dem Verbindungszustand
 * (pc.connectionState === 'connected'), NICHT auf dem Vorhandensein der optionalen
 * Cipher-Anzeige. Firefox z.B. weist dtlsCipher/srtpCipher nicht über getStats aus,
 * obwohl die Verbindung selbstverständlich verschlüsselt ist.
 *
 * Quelle ist die WebRTC-eigene getStats-API — keine Server-Beteiligung.
 */

export type ConnTransport = 'direct' | 'relay' | 'unknown';

export interface ConnectionInfo {
  /** ist die Peer-Connection verbunden? Bei true läuft DTLS-SRTP → verschlüsselt. */
  connected: boolean;
  /** direkte P2P-Verbindung oder über TURN-Relay */
  transport: ConnTransport;
  /** lokaler und entfernter ICE-Candidate-Typ, z.B. 'host', 'srflx', 'relay' */
  localCandidateType: string | null;
  remoteCandidateType: string | null;
  /** DTLS-Transport */
  dtlsState: string | null;
  dtlsCipher: string | null;
  srtpCipher: string | null;
  tlsVersion: string | null;
  /** Round-Trip-Time in Millisekunden (gerundet) */
  rttMs: number | null;
  /** ob die ausgehandelte Medien-Cipher als stark gilt (AES-GCM/ChaCha20). Nur
   *  aussagekräftig wenn der Browser die Cipher überhaupt ausweist. */
  cipherStrong: boolean;
  /** ob der Browser überhaupt Cipher-Details geliefert hat */
  cipherKnown: boolean;
  /** Welle 7c: ob die SFrame-E2EE-Schicht aktiv ist (beidseitig verschlüsselt) */
  e2eeActive?: boolean;
  /** Welle 7e: ob E2EE für diesen Call beabsichtigt war (Tenant-Policy). */
  e2eeIntended?: boolean;
  /** Welle 7e: ob E2EE bereit, aber pausiert ist (z.B. 3+ Teilnehmer/SFU). */
  e2eeSuspended?: boolean;
  /** Roh-Zeitstempel der Messung */
  sampledAt: number;
}

function isCipherStrong(srtp: string | null, _dtls: string | null): boolean {
  // Die Stärke der MEDIEN richtet sich allein nach der SRTP-Cipher. Die DTLS-
  // Handshake-Cipher ist bei Chromium fast immer GCM und schützt nur den
  // Schlüsselaustausch, nicht die Medien — daher hier bewusst NICHT einbezogen.
  const media = (srtp ?? '').toUpperCase();
  if (!media) return false; // SRTP-Cipher unbekannt → nicht als stark werten
  if (media.includes('GCM')) return true;
  if (media.includes('CHACHA20')) return true;
  return false;
}

function candidateTypeToTransport(local: string | null, remote: string | null): ConnTransport {
  if (!local || !remote) return 'unknown';
  if (local === 'relay' || remote === 'relay') return 'relay';
  return 'direct';
}

/** DTLS-Version aus dem tlsVersion-Feld (Chrome liefert Hex wie 'FEFD'). */
function normalizeTlsVersion(raw: string | null): string | null {
  if (!raw) return null;
  const v = raw.toUpperCase();
  // DTLS-Hex-Codes
  if (v === 'FEFD' || v === 'DTLS 1.2' || v === '1.2') return '1.2';
  if (v === 'FEFC' || v === 'DTLS 1.3' || v === '1.3') return '1.3';
  return raw;
}

export async function readConnectionInfo(pc: RTCPeerConnection): Promise<ConnectionInfo> {
  // Verbindungszustand zuerst aus der PC selbst — zuverlässigster Indikator,
  // unabhängig davon was getStats liefert.
  const pcState = pc.connectionState;
  const iceState = pc.iceConnectionState;
  const connected =
    pcState === 'connected' ||
    iceState === 'connected' ||
    iceState === 'completed';

  const info: ConnectionInfo = {
    connected,
    transport: 'unknown',
    localCandidateType: null,
    remoteCandidateType: null,
    dtlsState: connected ? 'connected' : null,
    dtlsCipher: null,
    srtpCipher: null,
    tlsVersion: null,
    rttMs: null,
    cipherStrong: false,
    cipherKnown: false,
    sampledAt: Date.now(),
  };

  let stats: RTCStatsReport;
  try {
    stats = await pc.getStats();
  } catch {
    return info;
  }

  const byId = new Map<string, any>();
  stats.forEach((r: any) => byId.set(r.id, r));

  // Aktives Candidate-Pair finden (Chrome: nominated; Firefox: selected)
  let activePair: any = null;
  stats.forEach((r: any) => {
    if (r.type === 'candidate-pair' && (r.nominated || r.selected || r.state === 'succeeded')) {
      if (!activePair || (r.bytesSent ?? 0) >= (activePair.bytesSent ?? 0)) {
        activePair = r;
      }
    }
  });
  if (!activePair) {
    stats.forEach((r: any) => {
      if (r.type === 'transport' && r.selectedCandidatePairId) {
        activePair = byId.get(r.selectedCandidatePairId);
      }
    });
  }

  if (activePair) {
    const local = byId.get(activePair.localCandidateId);
    const remote = byId.get(activePair.remoteCandidateId);
    info.localCandidateType = local?.candidateType ?? null;
    info.remoteCandidateType = remote?.candidateType ?? null;
    info.transport = candidateTypeToTransport(info.localCandidateType, info.remoteCandidateType);
    if (typeof activePair.currentRoundTripTime === 'number') {
      info.rttMs = Math.round(activePair.currentRoundTripTime * 1000);
    }
  }

  // Transport-Stat für DTLS/SRTP-Details (Chrome füllt das; Firefox oft nicht)
  stats.forEach((r: any) => {
    if (r.type === 'transport') {
      if (r.dtlsState) info.dtlsState = r.dtlsState;
      if (r.dtlsCipher) info.dtlsCipher = r.dtlsCipher;
      if (r.srtpCipher) info.srtpCipher = r.srtpCipher;
      if (r.tlsVersion) info.tlsVersion = normalizeTlsVersion(r.tlsVersion);
    }
  });

  info.cipherKnown = Boolean(info.dtlsCipher || info.srtpCipher);
  // cipherStrong nur dann false-aussagekräftig, wenn wir die Cipher überhaupt kennen.
  info.cipherStrong = info.cipherKnown ? isCipherStrong(info.srtpCipher, info.dtlsCipher) : false;
  return info;
}
